import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { StudyMaterialTypeEnum } from '@edtech-ui/constants/material-type.enum';
import { NotificationTypeEnum } from '@edtech-ui/constants/notification.enum';
import { IstDatePipe } from '@edtech-ui/core/models/Classroom/ist-date.pipe';
import { NotificationItem } from '@edtech-ui/core/models/notifications/get-notification-response';
import { MarkNotificationPayload } from '@edtech-ui/core/models/notifications/marked-notification-response';
import { NotificationService } from '@edtech-ui/core/services/notification-managament-services/notification.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { DeleteDialogBoxComponent } from '../../classroom/delete-dialog-box/delete-dialog-box.component';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { CompleteSyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-complete-syllabus';

@Component({
	selector: 'app-notifications',
	standalone: true,
	imports: [MatIconModule, CommonModule, MatListModule, MatMenuTrigger, IstDatePipe, MatProgressSpinnerModule,
		MatMenuModule, MatButtonModule
	],
	templateUrl: './notifications.component.html',
	styleUrl: './notifications.component.scss'
})
export class NotificationsComponent implements OnInit, OnDestroy {

	notificationTypeEnum = NotificationTypeEnum;
	subscription = new Subscription();
	studentNotifications: NotificationItem[] = [];
	totalNotificationCount: number = 0;
	@Output() totalCountChanged = new EventEmitter<number>();
	@Output() closeSlider = new EventEmitter<void>();
	isLoading: boolean = true;
	isNotificationOpen = false;
	@Input() isSliderMode: boolean = false;
	@Input() source: 'home' | 'notifications' = 'notifications';

	constructor(
		private _notificationService: NotificationService,
		private router: Router,
		private _toastr: ToastrService,
		private dialog: MatDialog,
		private _courseService: CourseManagementService
	) {
		this.subscription.add(this._notificationService.reload$.pipe(tap(() => this.getNotifications())).subscribe());
	}


	ngOnInit(): void {
		this.getNotifications();
	}

	getNotifications(): void {
		this.isLoading = true;
		this.subscription.add(
			this._notificationService.getNotificationsForStudent().pipe(
				tap(response => {
					if (response.Data && response.Success) {
						this.studentNotifications = response.Data;
						const unreadNotifications = response.Data.filter((n: any) => !n.is_read);
						this.totalNotificationCount = unreadNotifications.length;
						this.totalCountChanged.emit(this.totalNotificationCount);
					}
				}),
				catchError(() => {
					this.isLoading = false;
					return of(null);
				}),
				finalize(()=>{this.isLoading = false})
			).subscribe()
		);
	}

	putNotifications(notificationIds: string[]) {

		const unreadNotification = this.studentNotifications.filter(n => notificationIds.includes(n.id) && !n.is_read);
		if (!unreadNotification.length) return;

		const payload: MarkNotificationPayload = { notification_ids: unreadNotification.map(n => n.id) }

		this._notificationService.putMarkedNotifications(payload).pipe(
			tap(response => {
				if (response.Success) {
					unreadNotification.forEach(n => n.is_read = true);
				}
			}),
			catchError(err => of(null))
		).subscribe();
	}

	goToAnnouncementPage(class_id: number) {
		this.router.navigate(['student-dashboard/classroom-detail-page', class_id, 'overview'])
	}

	goToCourseDetailPage(class_id: number, content_id: string): void {
		this._courseService.getCoursesByCourseId(content_id).subscribe({
			next: (courseResponse: CompleteSyllabusResponseAI) => {
				const course = courseResponse.Data;

				if (!course) {
					this._toastr.error('Course not found');
					return;
				}

				const route = course.is_ai
					? ['student-dashboard/course-view', class_id, content_id]
					: ['student-dashboard/manual-course-view', class_id, content_id];

				this.router.navigate(route);
			},
			error: () => {
				this._toastr.error('Failed to load course details');
			}
		});
	}


	goToQuizPage(class_id: number) {
		this.router.navigate(['student-dashboard/classroom-detail-page', class_id, 'quiz'])
	}

	deleteNotification(notificationId: string) {
		this.subscription.add(
			this._notificationService.deleteNotification(notificationId).subscribe({
				next: () => {
					this._toastr.success('Notification deleted successfully');
					this.getNotifications();
				},
				error: (err) => {
					this._toastr.error('Failed to delete notification');
				}
			})
		);
	}


	openDeleteNotificationBox(notificationId: string) {
		this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { notificationId }
		}).afterClosed().subscribe(result => {
			if (result) {
				this.deleteNotification(notificationId);
			}
		});
	}

	openNotifications() {
		this.isNotificationOpen = !this.isNotificationOpen;
	}

	close() {
		if (this.isSliderMode) {
			this.closeSlider.emit();
		} else {
			this.router.navigate(['/student-dashboard/student-dashboard-content']);
		}
	}

	markAllNotificationsAsRead() {
		const unreadIds = this.studentNotifications.filter(n => !n.is_read).map(n => n.id);

		if (!unreadIds.length) return;
		this.putNotifications(unreadIds);
		this.totalNotificationCount = 0;
		this.totalCountChanged.emit(this.totalNotificationCount);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}